import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

export const CustomDatePicker = ({
  value,
  onChange,
  placeholder,
  isRTL = false,
}: {
  value: any;
  onChange?: (date: Dayjs | Date | string) => void;
  placeholder?: string;
  isRTL?: boolean;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const initialDate =
    value instanceof Date ? value : value ? new Date(value) : null;

  const [selectedDay, setSelectedDay] = useState(
    initialDate?.getDate() || null
  );
  const [selectedMonth, setSelectedMonth] = useState(
    initialDate?.getMonth() || null
  );
  const [selectedYear, setSelectedYear] = useState(
    initialDate?.getFullYear() || null
  );

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  // Refs to prevent infinite scroll loops
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 768); // md breakpoint
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Generate options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = t("inscription.datepicker.months", {
    returnObjects: true,
  }) as string[];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  // Format display value
  const getDisplayValue = () => {
    if (selectedDay && selectedMonth !== null && selectedYear) {
      return `${selectedDay.toString().padStart(2, "0")}/${(selectedMonth + 1)
        .toString()
        .padStart(2, "0")}/${selectedYear}`;
    }
    return "";
  };
  const scrollToCenter = (
    ref: { current: any },
    selectedIndex: number | null
  ) => {
    if (ref.current && selectedIndex !== null) {
      const container = ref.current;
      const itemHeight = 40;
      const containerHeight = container.clientHeight;
      const scrollTop =
        selectedIndex * itemHeight - containerHeight / 2 + itemHeight / 2;
      container.scrollTop = scrollTop;
    }
  };

  // Example: Initialize scroll when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        isScrollingRef.current = true;
        scrollToCenter(dayRef, selectedDay ? selectedDay - 1 : 14);
        scrollToCenter(monthRef, selectedMonth !== null ? selectedMonth : 5); // middle month
        scrollToCenter(yearRef, selectedYear ? currentYear - selectedYear : 25); // middle year
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 200);
      }, 50);
    }
  }, [isOpen]);

  // const scrollToCenter = (ref: { current: any; }, selectedIndex: number | null) => {
  //   if (ref.current && selectedIndex !== null) {
  //     const container = ref.current;
  //     const itemHeight = 40;
  //     const containerHeight = container.clientHeight;
  //     const scrollTop = selectedIndex * itemHeight - (containerHeight / 2) + (itemHeight / 2);
  //     container.scrollTop = Math.max(0, scrollTop);
  //   }
  // };

  // Calculate centered item based on scroll position
  // const getCenteredIndex = (scrollTop: number, itemHeight: number = 40, containerHeight: number) => {
  //   const centerPosition = scrollTop + (containerHeight / 2);
  //   return Math.round(centerPosition / itemHeight);
  // };
  const getCenteredIndex = (
    scrollTop: number,
    itemHeight: number = 40,
    containerHeight: number,
    paddingTop: number = 80
  ) => {
    const centerPosition = scrollTop + containerHeight / 2 - paddingTop;
    return Math.round(centerPosition / itemHeight);
  };

  // Debounced scroll handlers for automatic selection
  const handleDayScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isScrollingRef.current) return;

    const container = e.currentTarget;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const centeredIndex = getCenteredIndex(
        container.scrollTop,
        40,
        container.clientHeight,
        80
      );
      const dayValue = days[centeredIndex];
      if (dayValue && dayValue !== selectedDay) {
        setSelectedDay(dayValue);
      }
    }, 50);
  };

  const handleMonthScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isScrollingRef.current) return;

    const container = e.currentTarget;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const centeredIndex = getCenteredIndex(
        container.scrollTop,
        40,
        container.clientHeight
      );
      if (
        centeredIndex >= 0 &&
        centeredIndex < months.length &&
        centeredIndex !== selectedMonth
      ) {
        setSelectedMonth(centeredIndex);
      }
    }, 50);
  };

  const handleYearScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isScrollingRef.current) return;

    const container = e.currentTarget;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const centeredIndex = getCenteredIndex(
        container.scrollTop,
        40,
        container.clientHeight
      );
      const yearValue = years[centeredIndex];
      if (yearValue && yearValue !== selectedYear) {
        setSelectedYear(yearValue);
      }
    }, 50);
  };

  // Initialize scroll positions when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        isScrollingRef.current = true;
        scrollToCenter(dayRef, selectedDay ? selectedDay - 1 : 14);
        scrollToCenter(monthRef, selectedMonth !== null ? selectedMonth : 8);
        scrollToCenter(yearRef, selectedYear ? currentYear - selectedYear : 25);

        setTimeout(() => {
          isScrollingRef.current = false;
        }, 200);
      }, 50);
    }
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleDone = () => {
    if (selectedDay && selectedMonth !== null && selectedYear) {
      const date = new Date(selectedYear, selectedMonth, selectedDay);
      const formattedDate = dayjs(date);
      onChange?.(formattedDate);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {/* Input Display */}
      <div
        onClick={() => setIsOpen(true)}
        style={{
          width: "100%",
          height: "40px",
          border: "1px solid #d9d9d9",
          borderRadius: "6px",
          padding: "0 11px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "#fff",
          fontSize: "14px",
          color: getDisplayValue() ? "#000000d9" : "#00000040",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          (e.target as HTMLDivElement).style.borderColor = "#40a9ff";
        }}
        onMouseLeave={(e) => {
          (e.target as HTMLDivElement).style.borderColor = "#d9d9d9";
        }}
      >
        {getDisplayValue() || t("inscription.datepicker.placeholder")}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          style={{
            marginLeft: isRTL ? "0" : "auto",
            opacity: 0.45,
            marginRight: isRTL ? "auto" : "0",
          }}
        >
          <path
            d="M3.5 4.5L6 7l2.5-2.5"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
        </svg>
      </div>

      {/* Custom Picker Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1000,
            }}
            onClick={handleCancel}
          />

          {/* Picker Container - Responsive positioning */}
          <div
            style={{
              position: isSmallScreen ? "fixed" : "absolute",
              top: isSmallScreen ? "auto" : "100%",
              bottom: isSmallScreen ? 0 : "auto",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              borderRadius: isSmallScreen ? "20px 20px 0 0" : "12px",
              borderTopLeftRadius: isSmallScreen ? "20px" : "12px",
              borderTopRightRadius: isSmallScreen ? "20px" : "12px",
              borderBottomLeftRadius: isSmallScreen ? "0" : "12px",
              borderBottomRightRadius: isSmallScreen ? "0" : "12px",
              zIndex: 1001,
              maxHeight: isSmallScreen ? "50vh" : "400px",
              boxShadow: isSmallScreen
                ? "0 -4px 20px rgba(0, 0, 0, 0.1)"
                : "0 8px 24px rgba(0, 0, 0, 0.15)",
              border: isSmallScreen ? "none" : "1px solid #e8e8e8",
              marginTop: isSmallScreen ? "0" : "4px",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: isSmallScreen ? "20px 20px 10px" : "16px 20px 12px",
                textAlign: "center",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: isSmallScreen ? "18px" : "16px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                {placeholder || t("inscription.datepicker.placeholder")}
                <div
                  style={{
                    fontSize: isSmallScreen ? "18px" : "12px",
                    color: "#4096ff",
                    marginTop: isSmallScreen ? "4px" : "2px",
                    fontWeight: "800",
                    textAlign: "center",
                    backgroundColor: "#f0f8ff",
                    padding: isSmallScreen ? "4px 8px" : "2px 4px",
                    borderRadius: "8px",
                  }}
                >
                  {selectedDay && selectedMonth !== null && selectedYear ? (
                    `${getDisplayValue()}`
                  ) : (
                    <div style={{ color: "#999" }}>--/--/----</div>
                  )}
                </div>
              </h3>
            </div>

        {/* Date Columns */}
<div
  style={{
    display: "flex",
    height: "200px",
    padding: isSmallScreen ? "0 20px" : "0 16px",
    position: "relative", // <- make this relative so absolute highlight can sit on top
  }}
>
  {/* Days */}
  <div style={{ flex: 1 }}>
    <div
      ref={dayRef}
      onScroll={handleDayScroll}
      style={{
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        paddingTop: "80px",
        paddingBottom: "80px",
      }}
    >
      {days.map((day) => (
        <div
          key={day}
          onClick={() => {
            isScrollingRef.current = true;
            setSelectedDay(day);
            scrollToCenter(dayRef, day - 1);
            setTimeout(() => {
              isScrollingRef.current = false;
            }, 200);
          }}
          style={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: selectedDay === day ? "18px" : "16px",
            fontWeight: selectedDay === day ? "600" : "400",
            color: selectedDay === day ? "#4caf50" : "#666",
            transition: "all 0.2s",
          }}
        >
          {day}
        </div>
      ))}
    </div>
  </div>

  {/* Months */}
  <div style={{ flex: 2 }}>
    <div
      ref={monthRef}
      onScroll={handleMonthScroll}
      style={{
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        paddingTop: "80px",
        paddingBottom: "80px",
      }}
    >
      {months.map((month, index) => (
        <div
          key={month}
          onClick={() => {
            isScrollingRef.current = true;
            setSelectedMonth(index);
            scrollToCenter(monthRef, index);
            setTimeout(() => {
              isScrollingRef.current = false;
            }, 200);
          }}
          style={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: selectedMonth === index ? "18px" : "16px",
            fontWeight: selectedMonth === index ? "600" : "400",
            color: selectedMonth === index ? "#4caf50" : "#666",
            transition: "all 0.2s",
          }}
        >
          {month}
        </div>
      ))}
    </div>
  </div>

  {/* Years */}
  <div style={{ flex: 1 }}>
    <div
      ref={yearRef}
      onScroll={handleYearScroll}
      style={{
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        paddingTop: "80px",
        paddingBottom: "80px",
      }}
    >
      {years.map((year) => (
        <div
          key={year}
          onClick={() => {
            isScrollingRef.current = true;
            setSelectedYear(year);
            scrollToCenter(yearRef, currentYear - year);
            setTimeout(() => {
              isScrollingRef.current = false;
            }, 200);
          }}
          style={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: selectedYear === year ? "18px" : "16px",
            fontWeight: selectedYear === year ? "600" : "400",
            color: selectedYear === year ? "#4caf50" : "#666",
            transition: "all 0.2s",
          }}
        >
          {year}
        </div>
      ))}
    </div>
  </div>

  {/* Single Selection Highlight */}
  <div
    style={{
      position: "absolute",
      top: "60%",
      left: isSmallScreen ? "10px" : "8px",
      right: isSmallScreen ? "10px" : "8px",
      height: "40px",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      transform: "translateY(-50%)",
      pointerEvents: "none",
      zIndex: -1,
    }}
  />
</div>

            {/* Done Button */}
            <div style={{ padding: isSmallScreen ? "20px" : "16px" }}>
              <button
                onClick={handleDone}
                style={{
                  width: "100%",
                  height: isSmallScreen ? "50px" : "44px",
                  backgroundColor: "#4096ff",
                  color: "#fff",
                  border: "none",
                  borderRadius: isSmallScreen ? "25px" : "8px",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor =
                    "#1677ff";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor =
                    "#4096ff";
                }}
              >
                {t("inscription.datepicker.done")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
